import escapeStringRegexp from 'escape-string-regexp';
import { type SubmitHandler, useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import { SITE_BASENAME_MAX_LENGTH, SITE_BASENAME_MIN_LENGTH } from '@gitbook/api-client';
import {
    Description,
    FieldError,
    Form,
    Label,
    Submit,
    TextField,
    TextInput,
    TextInputTextPrefix,
    toast,
} from '@gitbook/spine-react';

import { apiClient } from '../../../../../state';

export type SiteEditSlugFormInputs = {
    slug: string;
};

/**
 * Form field to edit the slug for the site in settings
 */
export function EditSlug(props: { site: api.Site; organization: api.Organization }) {
    const { site, organization } = props;
    const siteSlug = site.basename ?? '';
    const form = useForm<SiteEditSlugFormInputs>({ defaultValues: { slug: siteSlug } });
    const baseURL = site.urls.published
        ? site.urls.published.replace(new RegExp(`${escapeStringRegexp(siteSlug)}/$`), '')
        : null;
    const onSubmit: SubmitHandler<SiteEditSlugFormInputs> = async (data) => {
        await toast.catch(async () => {
            await apiClient.sites.updateSiteById(organization.id, site.id, {
                basename: data.slug,
            });
            await form.reset({ slug: data.slug });
            toast.success('GitBook site URL updated');
        }, 'Error while updating GitBook URL');
    };
    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <TextField
                name="slug"
                minLength={SITE_BASENAME_MIN_LENGTH}
                maxLength={SITE_BASENAME_MAX_LENGTH}
            >
                <Label>Edit slug</Label>
                <Description>
                    Change your site’s slug to make the URL shorter, cleaner, and easier to
                    remember.
                </Description>
                <div className="flex gap-4">
                    <TextInput
                        prefix={baseURL ? <TextInputTextPrefix text={baseURL} /> : null}
                        readOnly={form.formState.isSubmitting}
                    />
                    <Submit label="Save" />
                </div>
                <FieldError />
            </TextField>
        </Form>
    );
}
